<template>
    <div class="flex w-full flex-col items-center justify-center px-4 py-6">
        <!-- Custom message (if provided) -->
        <div
            v-if="message"
            class="prose prose-sm mb-4 max-w-md text-center"
            v-html="message"
        />

        <div class="w-full max-w-md">
            <div class="flex flex-col items-center gap-4">
                <!-- SSO Login Buttons (one per provider) -->
                <DButton
                    v-for="provider in providers"
                    :key="getProviderKey(provider)"
                    type="button"
                    theme="brand"
                    size="md"
                    :loading="initiatingProviderKey === getProviderKey(provider)"
                    :disabled="isInitiating"
                    class="w-full"
                    @click="handleProviderClick(provider)"
                >
                    <span class="flex items-center justify-center gap-2">
                        {{ provider.name }}
                    </span>
                </DButton>

                <!-- Error message -->
                <p
                    v-if="displayError"
                    class="text-center text-xs text-red-600"
                >
                    {{ displayError }}
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { DButton } from '@digistorm/spark'

    interface SamlProvider {
        id?: number | null
        uid?: string | null
        name: string
    }

    const props = defineProps<{
        gateUid: string
        providers: Array<SamlProvider>
        message?: string | null
        entryTitle?: string
    }>()

    const localError = ref('')
    const initiatingProviderKey = ref<string | null>(null)

    // Initialize SAML flow composable only on client-side
    let samlFlowComposable: ReturnType<typeof useSamlFlow> | null = null
    const isInitiating = ref(false)
    const flowError = ref<string | null>(null)

    if (import.meta.client) {
        samlFlowComposable = useSamlFlow()
        isInitiating.value = samlFlowComposable.isInitiating.value
        flowError.value = samlFlowComposable.flowError.value

        // Watch for changes in composable state
        watch(
            () => samlFlowComposable?.isInitiating.value,
            (val) => {
                isInitiating.value = val ?? false
                // Clear the active provider key when the flow finishes
                if (!val) {
                    initiatingProviderKey.value = null
                }
            },
        )
        watch(
            () => samlFlowComposable?.flowError.value,
            (val) => {
                flowError.value = val ?? null
            },
        )
    }

    const displayError = computed(() => localError.value || flowError.value)

    const getProviderKey = (provider: SamlProvider): string => {
        if (provider.uid) {
            return provider.uid
        }

        if (provider.id != null) {
            return String(provider.id)
        }

        return provider.name
    }

    const handleProviderClick = async (provider: SamlProvider) => {
        localError.value = ''
        initiatingProviderKey.value = getProviderKey(provider)

        if (!samlFlowComposable) {
            localError.value = 'SSO authentication not available'
            initiatingProviderKey.value = null
            return
        }

        if (!import.meta.client) {
            localError.value = 'SSO requires client-side execution'
            initiatingProviderKey.value = null
            return
        }

        // Get the current URL to return to after authentication
        const returnUrl = window.location.href

        // Initiate the SAML flow for the selected provider
        const loginUrl = await samlFlowComposable.initiateSamlFlow(
            props.gateUid,
            {
                providerUid: provider.uid ?? null,
                providerId: provider.id ?? null,
            },
            returnUrl,
        )

        if (loginUrl) {
            // Redirect to IdP login page
            samlFlowComposable.redirectToIdp(loginUrl)
        } else {
            // Reset the active provider key on immediate failure.
            initiatingProviderKey.value = null
        }
    }
</script>
